import * as z from "zod";
import { BooleanSchema } from "../primitives/boolean";

/** Public hosts of the shop being revalidated; they also select its backend. */
const HostsSchema = z.array(z.string().trim().max(253)).optional();
const IdSchema = z.union([z.number(), z.string()]).pipe(z.coerce.number<string | number>().int().positive());

/** Fails the request when the edge purge fails, instead of only logging it. */
const RequireEdgeInvalidationSchema = BooleanSchema.optional();

export const RevalidateByTagsSchema = z.object({
  action: z.literal("revalidateByTags"),
  tags: z.array(z.string()),
  hosts: HostsSchema,
  pathname: z.string().max(2048).optional(),
  requireEdgeInvalidation: RequireEdgeInvalidationSchema,
});

export const StorefrontRevalidationSchema = z.discriminatedUnion("action", [
  RevalidateByTagsSchema,
  z.object({ action: z.literal("revalidateAll") }),
  z.object({ action: z.literal("revalidateByProductId"), productId: IdSchema, hosts: HostsSchema }),
  z.object({ action: z.literal("revalidateShopFavicon"), shopId: IdSchema, hosts: HostsSchema }),
  z.object({
    action: z.literal("revalidateByShopId"),
    shopId: IdSchema,
    hosts: HostsSchema,
    requireEdgeInvalidation: RequireEdgeInvalidationSchema,
  }),
  z.object({
    action: z.literal("revalidateSmartLink"),
    hosts: HostsSchema,
    slug: z.string(),
    requireEdgeInvalidation: RequireEdgeInvalidationSchema,
  }),
  // Funnel invalidation has no action; older backends send only funnelId.
  z.object({
    action: z.undefined().optional(),
    funnelId: IdSchema,
    hosts: HostsSchema,
    requireEdgeInvalidation: RequireEdgeInvalidationSchema,
  }),
]);

export type StorefrontRevalidationRequest = z.infer<typeof StorefrontRevalidationSchema>;

export const StorefrontRevalidationResultSchema = z.object({
  revalidated: z.boolean(),
  now: z.number(),
  message: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

export type StorefrontRevalidationResult = z.infer<typeof StorefrontRevalidationResultSchema>;
